/**
 * Distribuição geográfica da Membresia: os membros contados por bairro a partir do
 * endereço do cadastro (`address.district` e `address.city`). Por enquanto o catálogo é o
 * de **Natal/RN** (36 bairros, nas quatro zonas administrativas), com a posição aproximada
 * de cada um — o centro do bairro, não o limite. Quem não tem bairro de Natal informado
 * fica fora do mapa e é contado à parte.
 *
 * Privacidade: só contagens; bairro com menos de `MIN_GROUP` pessoas aparece como "<5";
 * os pontos por membro não levam nome nem id. As coordenadas (`Member.location`) saem do
 * endereço, buscadas pelo servidor (`addressKey` diz de qual endereço vieram).
 */
import { type DateRange, joinsIn, lastTwelveMonths, memberAt } from './community';
import { addDays, hashString, normalize } from './dates';
import { CHURCH_STATUS_LABEL, formatPhone } from './members';
import type { Address, GeoPrecision, Member, MemberLocation, Organization } from './types';

export type Zone = 'N' | 'L' | 'O' | 'S';

export const ZONE_LABEL: Record<Zone, string> = {
  N: 'Zona Norte',
  L: 'Zona Leste',
  O: 'Zona Oeste',
  S: 'Zona Sul',
};

export interface District {
  name: string;
  /** Nome curto para o mapa ("N. S. Apresentação"). */
  short: string;
  zone: Zone;
  /** Posição no mapa esquemático (coluna e linha da grade). */
  col: number;
  row: number;
  /** Centro aproximado do bairro. */
  lat: number;
  lon: number;
}

const d = (
  name: string,
  zone: Zone,
  col: number,
  row: number,
  lat: number,
  lon: number,
  short = name
): District => ({
  name,
  short,
  zone,
  col,
  row,
  lat,
  lon,
});

export const GEO_CITY = { name: 'Natal', uf: 'RN' } as const;

export const NATAL_DISTRICTS: District[] = [
  d('Lagoa Azul', 'N', 2, 1, -5.725, -35.255),
  d('Pajuçara', 'N', 3, 1, -5.733, -35.23),
  d('Nossa Senhora da Apresentação', 'N', 1, 2, -5.743, -35.27, 'N. S. Apresentação'),
  d('Potengi', 'N', 2, 2, -5.75, -35.248),
  d('Redinha', 'N', 3, 2, -5.745, -35.215),
  d('Igapó', 'N', 2, 3, -5.765, -35.258),
  d('Salinas', 'N', 3, 3, -5.77, -35.24),
  d('Santos Reis', 'L', 5, 4, -5.762, -35.2),
  d('Ribeira', 'L', 4, 5, -5.777, -35.205),
  d('Rocas', 'L', 5, 5, -5.77, -35.2),
  d('Praia do Meio', 'L', 6, 5, -5.772, -35.192),
  d('Cidade Alta', 'L', 4, 6, -5.787, -35.207),
  d('Petrópolis', 'L', 5, 6, -5.787, -35.199),
  d('Areia Preta', 'L', 6, 6, -5.786, -35.187),
  d('Alecrim', 'L', 4, 7, -5.799, -35.218),
  d('Tirol', 'L', 5, 7, -5.797, -35.205),
  d('Mãe Luiza', 'L', 6, 7, -5.795, -35.185),
  d('Lagoa Seca', 'L', 4, 8, -5.81, -35.222),
  d('Barro Vermelho', 'L', 5, 8, -5.808, -35.212),
  d('Nordeste', 'O', 2, 6, -5.788, -35.238),
  d('Quintas', 'O', 3, 6, -5.795, -35.232),
  d('Bom Pastor', 'O', 2, 7, -5.81, -35.245),
  d('Dix-Sept Rosado', 'O', 3, 7, -5.811, -35.23),
  d('Felipe Camarão', 'O', 1, 8, -5.825, -35.262),
  d('Cidade da Esperança', 'O', 2, 8, -5.83, -35.24, 'C. da Esperança'),
  d('Nossa Senhora de Nazaré', 'O', 3, 8, -5.818, -35.234, 'N. S. de Nazaré'),
  d('Guarapes', 'O', 1, 9, -5.818, -35.28),
  d('Cidade Nova', 'O', 2, 9, -5.84, -35.25),
  d('Planalto', 'O', 2, 10, -5.857, -35.255),
  d('Lagoa Nova', 'S', 4, 9, -5.823, -35.213),
  d('Nova Descoberta', 'S', 5, 9, -5.822, -35.196),
  d('Candelária', 'S', 4, 10, -5.838, -35.212),
  d('Capim Macio', 'S', 5, 10, -5.857, -35.196),
  d('Pitimbu', 'S', 3, 11, -5.86, -35.236),
  d('Neópolis', 'S', 4, 11, -5.871, -35.214),
  d('Ponta Negra', 'S', 5, 11, -5.876, -35.178),
];

/** Abaixo disso, o bairro aparece como "<5" e sem cor (evita identificar alguém). */
export const MIN_GROUP = 5;

/** "<5" para 1 a 4; o número nos demais. */
export function groupLabel(count: number): string {
  return count > 0 && count < MIN_GROUP ? '<5' : count.toLocaleString('pt-BR');
}

const KEYS = new Map(
  NATAL_DISTRICTS.flatMap((x) => [[normalize(x.name), x] as const, [normalize(x.short), x] as const])
);

/**
 * O bairro de Natal do endereço, pelo nome (sem acento, sem caixa; aceita o curto); `null`
 * quando a cidade não é Natal ou o bairro não está no catálogo.
 */
export function districtOf(address: Pick<Address, 'district' | 'city' | 'uf'>): District | null {
  if (normalize(address.city.trim()) !== 'natal') return null;
  if (address.uf && address.uf.toUpperCase() !== GEO_CITY.uf) return null;

  return KEYS.get(normalize(address.district.trim()).replace(/\s+/g, ' ')) ?? null;
}

/** Domicílio: rua, número e CEP iguais; sem rua, cada cadastro conta como um. */
function householdKey(member: Member): string {
  const a = member.address;

  return a.street.trim() ? `${normalize(a.street.trim())}|${a.number.trim()}|${a.cep}` : `id:${member.id}`;
}

export interface DistrictStats {
  district: District;
  /** Membros ativos no fim do período. */
  active: number;
  /** Os mesmos, 12 meses antes. */
  activeYearAgo: number;
  /** Entradas ("Membro desde") no período. */
  joins: number;
  /** Domicílios, pelos cadastros com o mesmo endereço. */
  households: number;
  /** Ativos no fim de cada um dos últimos 12 meses. */
  trend: number[];
}

export interface Geography {
  districts: DistrictStats[];
  /** Ativos no fim do período com bairro de Natal informado. */
  located: number;
  /** Ativos sem bairro de Natal informado (fora do mapa). */
  unlocated: number;
}

/** Os números de cada bairro no período (o bairro é o do cadastro hoje). */
export function geography(members: readonly Member[], range: DateRange): Geography {
  const byDistrict = new Map<string, Member[]>();
  let unlocated = 0;
  const yearAgo = addDays(range.to, -365);
  const ends = lastTwelveMonths(range.to).map((month) => month.last);

  for (const m of members) {
    const district = districtOf(m.address);

    if (!district) {
      if (memberAt(m, range.to)) unlocated += 1;
      continue;
    }

    byDistrict.set(district.name, [...(byDistrict.get(district.name) ?? []), m]);
  }

  const districts = NATAL_DISTRICTS.map((district): DistrictStats => {
    const list = byDistrict.get(district.name) ?? [];
    const active = list.filter((m) => memberAt(m, range.to));

    return {
      district,
      active: active.length,
      activeYearAgo: list.filter((m) => memberAt(m, yearAgo)).length,
      joins: joinsIn(list, range),
      households: new Set(active.map(householdKey)).size,
      trend: ends.map((end) => list.filter((m) => memberAt(m, end)).length),
    };
  });

  return { districts, located: districts.reduce((s, x) => s + x.active, 0), unlocated };
}

// --- Coordenadas do endereço ------------------------------------------------------------

/**
 * O endereço como chave da coordenada: rua, número, bairro, cidade, UF e CEP, sem acento
 * nem caixa. Mudou a chave, a coordenada guardada deixa de valer (o servidor busca outra).
 * Sem rua, bairro nem cidade, `''` — não há o que localizar.
 */
export function addressKey(address: Address): string {
  const parts = [address.street, address.number, address.district, address.city, address.uf, address.cep].map(
    (p) => normalize(p.trim()).replace(/\s+/g, ' ')
  );

  return parts[0] || parts[2] || parts[3] ? parts.join('|') : '';
}

/** Distância em linha reta entre dois pontos, em quilômetros (fórmula de haversine). */
export function distanceKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const rad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;

  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

/** Até onde, a partir da igreja, um membro aparece no mapa (e no enquadramento dele). */
export const FOCUS_RADIUS_KM = 60;

/**
 * Os pontos que o mapa mostra e enquadra: os que ficam a até `FOCUS_RADIUS_KM` da igreja
 * ou, sem ela, da mediana dos pontos (que um endereço distante não arrasta). Quem mora em
 * outro estado fica fora do mapa — senão esticaria o enquadramento (o centro de Natal a São
 * Paulo cairia no interior da Bahia) e as linhas até a igreja. Sem nenhum perto, todos.
 */
export function focusPoints<T extends { lat: number; lon: number }>(
  points: readonly T[],
  church: { lat: number; lon: number } | null
): T[] {
  if (!points.length) return [];

  const median = (values: number[]) => {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = sorted.length >> 1;

    return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
  };
  const center = church ?? { lat: median(points.map((p) => p.lat)), lon: median(points.map((p) => p.lon)) };
  const near = points.filter((p) => distanceKm(center, p) <= FOCUS_RADIUS_KM);

  return near.length ? near : [...points];
}

/** A coordenada do membro, se é do endereço que ele tem hoje; senão, `null`. */
export function currentLocation(member: Pick<Member, 'address' | 'location'>): MemberLocation | null {
  const key = addressKey(member.address);

  return member.location && key && member.location.addressKey === key ? member.location : null;
}

/**
 * A coordenada da igreja, se é do endereço que ela tem hoje (o servidor busca outra quando
 * o endereço muda; até lá a antiga não vale); senão, `null` — o mapa não mostra o pin.
 */
export function orgLocation(org: Pick<Organization, 'address' | 'location'>): MemberLocation | null {
  return currentLocation({ address: org.address, location: org.location });
}

/** O membro precisa de coordenada nova: tem endereço e a guardada é de outro (ou nenhuma). */
export function needsGeocoding(member: Pick<Member, 'address' | 'location'>): boolean {
  return addressKey(member.address) !== '' && currentLocation(member) === null;
}

/**
 * Ao salvar o cadastro: a coordenada só fica se o endereço é o mesmo de onde ela veio — o
 * endereço novo fica sem coordenada até o servidor buscar.
 */
export function keepLocation<T extends Pick<Member, 'address' | 'location'>>(member: T): T {
  return member.location && !currentLocation(member) ? { ...member, location: null } : member;
}

export const GEO_PRECISION_LABEL: Record<GeoPrecision, string> = {
  address: 'Pelo número',
  street: 'Pela rua',
  district: 'Pelo bairro',
  city: 'Pela cidade',
};

export interface SampleHome {
  street: string;
  district: string;
  city: string;
  uf: string;
  cep: string;
  lat: number;
  lon: number;
}

const home = (
  street: string,
  district: string,
  cep: string,
  lat: number,
  lon: number,
  city = 'Natal'
): SampleHome => ({
  street,
  district,
  city,
  uf: 'RN',
  cep,
  lat,
  lon,
});

/**
 * Endereços de exemplo em Natal (ruas reais; o número é fictício) com a coordenada da rua
 * que o OpenStreetMap (Nominatim) devolveu e o bairro que ele indicou. Usados pelos dados
 * de exemplo e pelo complemento do banco de demonstração; uma casa fica em Parnamirim,
 * para o mapa mostrar quem mora na cidade vizinha.
 */
export const NATAL_SAMPLE_HOMES: readonly SampleHome[] = [
  home('Avenida Prudente de Morais', 'Lagoa Nova', '59064300', -5.8226884, -35.2140908),
  home('Avenida Nascimento de Castro', 'Lagoa Nova', '59056450', -5.812974, -35.2211846),
  home('Avenida Senador Salgado Filho', 'Tirol', '59056000', -5.8088722, -35.2037418),
  home('Avenida Hermes da Fonseca', 'Tirol', '59020000', -5.7906243, -35.1969985),
  home('Rua Apodi', 'Tirol', '59020130', -5.7924177, -35.1982269),
  home('Avenida Deodoro da Fonseca', 'Tirol', '59020600', -5.7905979, -35.2058403),
  home('Avenida Engenheiro Roberto Freire', 'Ponta Negra', '59078600', -5.8691436, -35.1818774),
  home('Avenida Erivan França', 'Ponta Negra', '59090165', -5.8806612, -35.1721682),
  home('Avenida Praia de Muriú', 'Ponta Negra', '59092350', -5.8816979, -35.1820658),
  home('Rua Missionário Joel Carlson', 'Capim Macio', '59082020', -5.8623657, -35.1979761),
  home('Avenida Ayrton Senna', 'Capim Macio', '59088100', -5.8614385, -35.2054436),
  home('Rua Tenente Belmiro Medeiros de Oliveira', 'Capim Macio', '59078570', -5.8563111, -35.2012861),
  home('Rua Seridó', 'Petrópolis', '59020010', -5.783513, -35.1989633),
  home('Rua Mossoró', 'Petrópolis', '59020090', -5.7869691, -35.2020823),
  home('Rua Jaguarari', 'Alecrim', '59030500', -5.808186, -35.2144958),
  home('Avenida Presidente Bandeira', 'Alecrim', '59031200', -5.7942247, -35.2189442),
  home('Rua Doutor Múcio Galvão', 'Barro Vermelho', '59022530', -5.804141, -35.2060637),
  home('Rua Raimundo Chaves', 'Candelária', '59064390', -5.829091, -35.223718),
  home('Avenida das Fronteiras', 'Potengi', '59108200', -5.7522417, -35.2627292),
  home('Rua Pedro Afonso', 'Praia do Meio', '59010150', -5.7768405, -35.1962694),
  home('Avenida Nevaldo Rocha', 'Lagoa Seca', '59022120', -5.8114614, -35.2085473),
  home(
    'Avenida Maria Lacerda Montenegro',
    'Nova Parnamirim',
    '59152600',
    -5.8775978,
    -35.2176556,
    'Parnamirim'
  ),
  // Zona Norte (índices 22 a 32) — os índices anteriores são usados pelo seed: só se acrescenta.
  home('Avenida Doutor João Medeiros Filho', 'Redinha', '59122232', -5.7462393, -35.2099872),
  home('Avenida Doutor João Medeiros Filho', 'Pajuçara', '59122475', -5.74951, -35.23076),
  home('Avenida Doutor João Medeiros Filho', 'Potengi', '59104220', -5.76781, -35.25478),
  home('Avenida Itapetinga', 'Potengi', '59122200', -5.75122, -35.24373),
  home('Avenida Rio Doce', 'Potengi', '59129600', -5.74204, -35.25544),
  home('Avenida Tomaz Landim', 'Nossa Senhora da Apresentação', '59114840', -5.76631, -35.2787),
  home('Avenida Tomaz Landim', 'Igapó', '59104220', -5.77332, -35.2601),
  home('Avenida das Fronteiras', 'Potengi', '59114035', -5.76057, -35.26462),
  home('Avenida das Fronteiras', 'Nossa Senhora da Apresentação', '59112490', -5.74816, -35.26126),
  home('Avenida Pedro Álvares Cabral', 'Nossa Senhora da Apresentação', '59115276', -5.75906, -35.27983),
  home('Rua São José', 'Nossa Senhora da Apresentação', '59115581', -5.73598, -35.2834),
  // Zona Oeste (índices 33 a 42)
  home('Rua Doutor Mário Negócio', 'Quintas', '59040000', -5.79733, -35.23216),
  home('Rua Doutor Mário Negócio', 'Nordeste', '59035027', -5.79866, -35.23761),
  home('Avenida Felizardo Moura', 'Nordeste', '59035027', -5.79423, -35.24171),
  home('Avenida Capitão-Mor Gouveia', 'Felipe Camarão', '59072130', -5.8179, -35.2422),
  home('Avenida Capitão-Mor Gouveia', 'Nossa Senhora de Nazaré', '59070400', -5.82293, -35.22854),
  home('Avenida Capitão-Mor Gouveia', 'Cidade da Esperança', '59070030', -5.82193, -35.2315),
  home('Avenida Jerônimo Câmara', 'Nossa Senhora de Nazaré', '59060420', -5.82073, -35.22772),
  home('Rua Belém', 'Cidade da Esperança', '59060400', -5.8233, -35.23575),
  home('Avenida Brasil', 'Felipe Camarão', '59072740', -5.8235, -35.24529),
  home('Avenida Amintas Barros', 'Dix-Sept Rosado', '59062600', -5.8135, -35.22805),
];

/**
 * O endereço da igreja de exemplo (Comunidade Esperança), em Lagoa Nova, Natal: a rua é
 * real e o número é fictício. Dá ao mapa da Membresia o pin da igreja.
 */
export const SAMPLE_CHURCH_HOME = NATAL_SAMPLE_HOMES[0]!;
export const SAMPLE_CHURCH_NUMBER = '1210';

/** O endereço e a coordenada de uma casa de exemplo, com o número da casa. */
export function sampleAddress(h: SampleHome, number: string, complement = ''): Address {
  return { cep: h.cep, street: h.street, number, complement, district: h.district, city: h.city, uf: h.uf };
}

export function sampleLocation(h: SampleHome, number: string, at: number): MemberLocation {
  return {
    lat: h.lat,
    lon: h.lon,
    precision: 'street',
    addressKey: addressKey(sampleAddress(h, number)),
    at,
  };
}

export interface MemberPoint {
  /** Chave estável (não é o id do membro: o ponto não identifica ninguém). */
  key: string;
  /** O bairro de Natal do endereço (`''` fora de Natal, como em Parnamirim). */
  district: string;
  lat: number;
  lon: number;
  /** `true`: veio da coordenada do endereço; `false`: posição aproximada no bairro. */
  located: boolean;
  /** Quem mora no ponto: o endereço e os dados básicos, para o popup do Administrador. */
  home: string;
  who: PointPerson;
}

export const homeLabel = (m: Pick<Member, 'address'>): string =>
  [m.address.street, m.address.number].filter(Boolean).join(', ');

/** Os dados básicos de quem mora no ponto (e na lista do grupo), para o Administrador. */
export function pointPerson(m: Member): PointPerson {
  return {
    id: m.id,
    name: m.nickname || m.name,
    status: m.churchStatus ? CHURCH_STATUS_LABEL[m.churchStatus] : '',
    phone: formatPhone(m.phone),
    photo: m.photo ?? null,
  };
}

export interface PointPerson {
  /** Para abrir o perfil: só o popup do Administrador o recebe (o ponto em si não tem id). */
  id: string;
  name: string;
  /** Situação na igreja (`CHURCH_STATUS_LABEL`). */
  status: string;
  phone: string;
  /** A foto do cadastro (`null`: as iniciais). */
  photo: string | null;
}

/**
 * Um ponto por membro ativo: na coordenada do endereço, quando o servidor já a obteve
 * (`currentLocation`, também fora de Natal), ou espalhado em volta do centro do bairro de
 * Natal (até ~500 m) enquanto não há coordenada. O ponto não leva o id do membro (o nome
 * e os dados básicos vão só para o popup do Administrador); a ordem é embaralhada pelo hash.
 */
export function memberPoints(members: readonly Member[], date: string): MemberPoint[] {
  return members
    .flatMap((m): MemberPoint[] => {
      if (!memberAt(m, date)) return [];

      const district = districtOf(m.address);
      const location = currentLocation(m);
      const h = hashString(`${m.id}geo`);
      const key = String(h);
      const home = homeLabel(m);
      const who = pointPerson(m);

      if (location) {
        // A coordenada da rua é a mesma para os vizinhos: um leve deslocamento (~60 m)
        // separa os pontos sem mudar de quarteirão.
        const angle = ((h % 3600) / 3600) * 2 * Math.PI;
        const radius = location.precision === 'address' ? 0 : 0.00025 + ((h >>> 12) % 100) / 200_000;

        return [
          {
            key,
            district: district?.name ?? '',
            lat: location.lat + Math.sin(angle) * radius,
            lon: location.lon + Math.cos(angle) * radius,
            located: true,
            home,
            who,
          },
        ];
      }

      if (!district) return [];

      const angle = ((h % 3600) / 3600) * 2 * Math.PI;
      const radius = 0.0012 + ((Math.floor(h / 3600) % 1000) / 1000) * 0.0033;

      return [
        {
          key,
          district: district.name,
          lat: district.lat + Math.sin(angle) * radius,
          lon: district.lon + Math.cos(angle) * radius,
          located: false,
          home,
          who,
        },
      ];
    })
    .sort((a, b) => a.key.localeCompare(b.key));
}

// --- Busca das coordenadas (geocodificação) --------------------------------------------

const STATE_NAME: Record<string, string> = {
  AC: 'Acre',
  AL: 'Alagoas',
  AP: 'Amapá',
  AM: 'Amazonas',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MT: 'Mato Grosso',
  MS: 'Mato Grosso do Sul',
  MG: 'Minas Gerais',
  PA: 'Pará',
  PB: 'Paraíba',
  PR: 'Paraná',
  PE: 'Pernambuco',
  PI: 'Piauí',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RS: 'Rio Grande do Sul',
  RO: 'Rondônia',
  RR: 'Roraima',
  SC: 'Santa Catarina',
  SP: 'São Paulo',
  SE: 'Sergipe',
  TO: 'Tocantins',
};

export interface GeocodingQuery {
  /** Parâmetros da busca estruturada do Nominatim (`street`, `city`, `state`, `country`…). */
  params: Record<string, string>;
  /** A precisão do resultado, se esta busca encontrar. */
  precision: GeoPrecision;
}

/**
 * As buscas, da mais precisa à mais larga: o número e a rua, só a rua, o bairro e, por
 * fim, só a cidade. Para na primeira que encontra. Sem cidade não há o que buscar (rua
 * igual existe em muitas cidades). Só o endereço vai na busca — nunca o nome da pessoa.
 */
export function geocodingQueries(address: Address): GeocodingQuery[] {
  const city = address.city.trim();

  if (!city) return [];

  const base: Record<string, string> = { city, country: 'Brasil' };
  const state = STATE_NAME[address.uf.toUpperCase()];

  if (state) base.state = state;

  const street = address.street.trim();
  const number = address.number.trim();
  const district = address.district.trim();
  const queries: GeocodingQuery[] = [];

  if (street && number)
    queries.push({ params: { ...base, street: `${number} ${street}` }, precision: 'address' });
  if (street) queries.push({ params: { ...base, street }, precision: 'street' });
  if (district) {
    queries.push({
      params: { q: [district, city, state ?? address.uf, 'Brasil'].filter(Boolean).join(', ') },
      precision: 'district',
    });
  }
  queries.push({ params: base, precision: 'city' });

  return queries;
}
